/*
 * Server-side feed document. See src/feed/feed.md ("Server feed").
 *
 * Fetched once per session from the first URL that answers. The bundled copy
 * in public/feed-index/ is only a fallback for offline / first install.
 */
import { once } from './util';
import {
  ITEM_TYPES,
  SUPPORTED_VERSION,
  sanitizeConfig,
  sanitizeServerItem,
} from './shared/validate.js';

// Validation lives in ./shared/validate.js (also used by the Cloud Functions).
export { ITEM_TYPES, sanitizeServerItem };

export const SERVER_FEED_URLS = [
  process.env.REACT_APP_FEED_URL || 'https://baws.in/assets/feed/feed.json',
  '/feed-index/server-feed.json',
];

const TIMEOUT_MS = 5000;

async function fetchJson(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      cache: 'no-cache',
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

/** { items, config, url } — items sanitized, config with safe defaults. */
export const loadServerFeed = once(async () => {
  let lastError;
  for (const url of SERVER_FEED_URLS) {
    try {
      const doc = await fetchJson(url);
      if (!doc || doc.version !== SUPPORTED_VERSION) {
        throw new Error(`${url}: unsupported feed version ${doc?.version}`);
      }
      return {
        url,
        items: (Array.isArray(doc.items) ? doc.items : [])
          .map(sanitizeServerItem)
          .filter(Boolean),
        config: sanitizeConfig(doc.config),
      };
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError || new Error('server feed unavailable');
});

// Config only; never rejects (the feed works without the server).
export const loadServerConfig = () =>
  loadServerFeed()
    .then((feed) => feed.config)
    .catch(() => sanitizeConfig(null));

/*
 * Archive of older server feed items (functions/src/publish/archive.js):
 *   archive/index.json        { pages: [{ date, path, count }] } newest first
 *   archive/<YYYY-MM-DD>.json { date, items }
 * next to feed.json. Readers page back through it at the end of /feed.
 */
const archiveUrl = (relPath) => new URL(relPath, SERVER_FEED_URLS[0]).href;

export const loadArchiveIndex = once(async () => {
  const doc = await fetchJson(archiveUrl('archive/index.json'));
  return Array.isArray(doc?.pages) ? doc.pages : [];
});

/** One archived day: sanitized items, marked `archived`. */
export async function loadArchivePage(page) {
  const doc = await fetchJson(archiveUrl(page.path));
  return (Array.isArray(doc?.items) ? doc.items : [])
    .map(sanitizeServerItem)
    .filter(Boolean)
    .map(({ startAt, endAt, audience, ...item }) => ({
      ...item,
      archived: true,
    }));
}
